import React, { useState, useEffect, useCallback } from 'react';
import { User } from '../../types';
import { jailService, JailedUser } from '../../services/jailService';
import { formatTime } from '../../utils/gameData';
import EscapePrison from './EscapePrison';

interface JailPageProps {
  user: User;
  onUpdate: () => void;
}

const JailPage: React.FC<JailPageProps> = ({ user, onUpdate }) => {
  const [jailedUsers, setJailedUsers] = useState<JailedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [bustingUser, setBustingUser] = useState<number | null>(null);
  const [showEscapeGame, setShowEscapeGame] = useState(false);

  // Refresh jailed users data
  const loadJailedUsers = useCallback(async () => {
    try {
      setLoading(true);
      const users = await jailService.getJailedUsers();
      setJailedUsers(users);
      setError(null);
    } catch (err) {
      setError('Failed to load jailed users');
      console.error('Error loading jailed users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadJailedUsers();
  }, [loadJailedUsers]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadJailedUsers, 30000);
    return () => clearInterval(interval);
  }, [loadJailedUsers]);

  // Handle setting bust reward
  const handleSetReward = useCallback(async (userId: number, reward: number) => {
    try {
      await jailService.setBustReward(userId, reward);
      await loadJailedUsers(); // Refresh data
    } catch (err) {
      setError('Failed to set reward');
      console.error('Error setting reward:', err);
    }
  }, [loadJailedUsers]);

  // Handle busting a user
  const handleBustUser = useCallback(async (jailedUser: JailedUser) => {
    if (bustingUser) return; // Prevent multiple busts

    try {
      setBustingUser(jailedUser.id);
      const result = await jailService.bustUser(jailedUser.id, jailedUser.reward);
      
      if (result.success) {
        alert(`Successfully busted ${jailedUser.username} out of jail! You earned $${result.reward?.toLocaleString()}`);
        await loadJailedUsers(); // Refresh data
        onUpdate(); // Refresh user stats (money, rank points)
      } else {
        alert(`Failed to bust ${jailedUser.username}: ${result.message}`);
      }
    } catch (err) {
      setError('Failed to bust user');
      console.error('Error busting user:', err);
    } finally {
      setBustingUser(null);
    }
  }, [bustingUser, loadJailedUsers, onUpdate]);

  // Calculate bust chance for current user
  const getBustChance = (jailedUser: JailedUser) => {
    return jailService.calculateBustChance(jailedUser.rank, user.rankId);
  };

  // Handle escape from jail
  const handleEscape = useCallback(() => {
    setShowEscapeGame(false);
    loadJailedUsers(); // Refresh data
    onUpdate(); // Refresh user stats
    window.location.reload(); // Refresh the page to update user status
  }, [loadJailedUsers, onUpdate]);

  // Format time remaining
  const formatTimeRemaining = (seconds: number) => {
    if (seconds <= 0) return 'Time served';
    return formatTime(seconds);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">Loading jailed users...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🚔 Jail</h1>
        <p>Current inmates in the system</p>
      </div>

      {error && (
        <div className="error-notification">
          {error}
        </div>
      )}

      {jailedUsers.length === 0 ? (
        <div className="empty-state">
          <h3>🎉 No one is currently in jail!</h3>
          <p>The streets are clean... for now.</p>
        </div>
      ) : (
        <div className="jail-table-container">
          <table className="jail-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Rank</th>
                <th>Crime</th>
                <th>Time Remaining</th>
                <th>Bust Reward</th>
                <th>Bust Chance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jailedUsers.map((jailedUser) => {
                const bustChance = getBustChance(jailedUser);
                const isCurrentUser = jailedUser.id.toString() === user._id;
                
                return (
                  <tr key={jailedUser.id} className={jailedUser.timeRemaining <= 0 ? 'time-served' : ''}>
                    <td>
                      <span className="username">{jailedUser.username}</span>
                      {isCurrentUser && <span className="current-user-badge">(You)</span>}
                    </td>
                    <td>
                      <span className="rank">{jailedUser.rankName}</span>
                      <span className="rank-number">(Level {jailedUser.rank})</span>
                    </td>
                    <td>
                      <span className="crime">{jailedUser.crime}</span>
                    </td>
                    <td>
                      <span className="time-remaining">
                        {formatTimeRemaining(jailedUser.timeRemaining)}
                      </span>
                    </td>
                    <td>
                      <div className="reward-display">
                        <span className="reward-amount">${jailedUser.reward.toLocaleString()}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`bust-chance ${bustChance >= 70 ? 'high' : bustChance >= 40 ? 'medium' : 'low'}`}>
                        {bustChance}%
                      </span>
                    </td>
                    <td>
                                          <button
                      className={`btn btn-bust ${bustChance >= 70 ? 'btn-success' : bustChance >= 40 ? 'btn-warning' : 'btn-danger'}`}
                      onClick={() => handleBustUser(jailedUser)}
                      disabled={bustingUser === jailedUser.id || jailedUser.timeRemaining <= 0 || isCurrentUser || user.status === 'Jailed'}
                      title={
                        isCurrentUser ? "You cannot bust yourself out of jail!" : 
                        user.status === 'Jailed' ? "You cannot bust others while in jail!" :
                        `${bustChance}% chance to bust ${jailedUser.username}`
                      }
                    >
                      {bustingUser === jailedUser.id ? 'Busting...' : 
                       isCurrentUser ? 'Cannot Bust Self' : 
                       user.status === 'Jailed' ? 'You Are Jailed' : 'Bust Out'}
                    </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Bust Reward Section - Only show if current user is in jail */}
      {user.status === 'Jailed' && (
        <div className="bust-reward-section">
          <div className="section-header">
            <h3>💰 Set Your Bust Reward</h3>
            <p>Set how much money you'll pay someone to bust you out of jail</p>
          </div>
          <div className="reward-input-container">
            <label htmlFor="bustReward">Bust Reward Amount:</label>
            <div className="reward-input-group">
              <span className="currency-symbol">$</span>
              <input
                id="bustReward"
                type="number"
                min="0"
                placeholder="Enter reward amount"
                className="reward-input"
                defaultValue={jailedUsers.find(u => u.id.toString() === user._id)?.reward || 0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const value = parseInt((e.target as HTMLInputElement).value);
                    handleSetReward(parseInt(user._id), value);
                  }
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  handleSetReward(parseInt(user._id), value);
                }}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                const input = document.getElementById('bustReward') as HTMLInputElement;
                const value = parseInt(input.value);
                handleSetReward(parseInt(user._id), value);
              }}
            >
              Update Reward
            </button>
          </div>
          
          {/* Escape Prison Button */}
          <div className="escape-prison-section">
            <h4>🔓 Try to Escape</h4>
            <p>Attempt to escape from jail by finding the hidden keys!</p>
            <button
              className="btn btn-warning"
              onClick={() => setShowEscapeGame(true)}
            >
              Play Escape Game
            </button>
          </div>
        </div>
      )}

      {/* Escape Prison Modal */}
      {showEscapeGame && (
                <EscapePrison
          user={user}
          onEscape={handleEscape}
          onClose={() => setShowEscapeGame(false)}
        />
      )}
    </div>
  );
};

export default JailPage;
